import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FLAG_DEFAULTS, type FlagKey, type Flags } from './defaults';
import { startGrowthBook, type FlagSource, type GrowthBookOptions } from './growthbook';

/**
 * GrowthBook-shaped flag access: `useFeatureIsOn(key)` and `useFeatureValue(key, fallback)`
 * mirror @growthbook/growthbook-react, so screens never see where a value comes from.
 * A value is, in order: an in-memory override (the dev drawer, tests), GrowthBook's (once it
 * has loaded, when a client key is set), the local default (`defaults.ts`).
 */
interface FlagsState {
  read: <K extends FlagKey>(key: K) => Flags[K];
  /** Every flag's current value (the dev drawer; reading it evaluates every flag). */
  readonly values: Flags;
  setOverride: <K extends FlagKey>(key: K, value: Flags[K] | undefined) => void;
}

const defaultState: FlagsState = {
  read: (key) => FLAG_DEFAULTS[key],
  values: FLAG_DEFAULTS,
  setOverride: () => {},
};
const FlagsContext = createContext<FlagsState>(defaultState);

export interface FlagsProviderProps {
  overrides?: Partial<Flags>;
  /** GrowthBook: from the env in the app, absent in tests (local defaults only). */
  growthbook?: GrowthBookOptions | null;
  /** Tests inject a source instead of loading the SDK. */
  start?: (o: GrowthBookOptions) => Promise<FlagSource>;
  children: ReactNode;
}

export function FlagsProvider({
  overrides: initial = {},
  growthbook = null,
  start = startGrowthBook,
  children,
}: FlagsProviderProps) {
  const [overrides, setOverrides] = useState<Partial<Flags>>(initial);
  const [source, setSource] = useState<FlagSource | null>(null);

  useEffect(() => {
    if (!growthbook?.clientKey) return;
    let gone = false;
    start(growthbook)
      .then((s) => !gone && setSource(s))
      .catch(() => {
        /* GrowthBook unreachable: local defaults */
      });
    return () => {
      gone = true;
    };
    // Started once per page; the options are fixed for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<FlagsState>(() => {
    const read = <K extends FlagKey>(key: K): Flags[K] =>
      (overrides[key] as Flags[K] | undefined) ?? source?.get(key) ?? FLAG_DEFAULTS[key];
    return {
      read,
      get values() {
        return Object.fromEntries(Object.keys(FLAG_DEFAULTS).map((k) => [k, read(k as FlagKey)])) as Flags;
      },
      setOverride: (key, v) =>
        setOverrides((o) => {
          const next = { ...o };
          if (v === undefined) delete next[key];
          else next[key] = v;
          return next;
        }),
    };
  }, [overrides, source]);
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

export function useFeatureValue<K extends FlagKey>(key: K, fallback?: Flags[K]): Flags[K] {
  const v = useContext(FlagsContext).read(key);
  return (v ?? fallback) as Flags[K];
}

export function useFeatureIsOn(key: FlagKey): boolean {
  return Boolean(useContext(FlagsContext).read(key));
}

export function useFlags(): FlagsState {
  return useContext(FlagsContext);
}
